/**
 * Sidebar — left navigation rail from mockup v12.
 *
 * Phase 4.2 Bucata 2.A.bis2 update: every item is now ENABLED — clicking
 * navigates to either the real page (Dashboard) or a PlaceholderPage with
 * the feature's phase tag and roadmap. This lets the user explore the
 * full surface of the platform.
 *
 * Responsive (Pas 1a): the navigation body is extracted into <SidebarNav> so
 * it can be reused verbatim by both the desktop <aside> (hidden below md) and a
 * mobile drawer (rendered from AppLayout). Single source of truth for the nav
 * list — adding a link to GROUPS surfaces it on desktop AND mobile at once.
 * The desktop markup is unchanged: same <aside hidden md:flex>, same Logo, same
 * NavRow/NavBadge. SidebarNav takes an optional onNavigate callback so the
 * drawer can close itself when a link is tapped; on desktop it is undefined and
 * NavRow behaves exactly as before.
 *
 * Logo: real sendSMS logo image (D-BRAND-LOGO), no text suffix.
 */
import { NavLink } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Home,
  Ticket,
  ShieldCheck,
  BadgeCheck,
  Paperclip,
  MessageSquare,
  Send,
  FileText,
  Inbox,
  Tag,
  Ban,
  Users,
  BarChart3,
  ArrowRightLeft,
  Lock,
  QrCode,
  Link2,
  AtSign,
  Forward,
  Megaphone,
  Phone,
  CreditCard,
  Wallet,
  Receipt,
  Tags,
  KeyRound,
  Webhook,
  Terminal,
  Plug,
  HelpCircle,
  Settings,
  Upload,
  type LucideIcon,
ScrollText, Activity, } from "lucide-react";

import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth/context";

type NavItem = {
  to: string;
  icon: LucideIcon;
  i18nKey: string;
  badge?: { kind: "count"; value: string } | { kind: "tag"; key: string };
};

type NavGroup = {
  /** Optional uppercase header shown above the group. */
  i18nKey?: string;
  items: NavItem[];
};

const GROUPS: NavGroup[] = [
  {
    items: [
      { to: "/dashboard", icon: Home, i18nKey: "nav.dashboard" },
    ],
  },
  {
    i18nKey: "nav.section.messaging",
    items: [
      { to: "/send", icon: MessageSquare, i18nKey: "nav.send_message" },
      { to: "/messages", icon: Inbox, i18nKey: "nav.messages_history" },
      { to: "/campaigns", icon: Send, i18nKey: "nav.campaigns" },
      { to: "/templates", icon: FileText, i18nKey: "nav.templates" },
      { to: "/inbox", icon: Inbox, i18nKey: "nav.inbox" },
      { to: "/senders", icon: Tag, i18nKey: "nav.senders" },
      { to: "/blocklist", icon: Ban, i18nKey: "nav.blocklist" },
    ],
  },
  {
    items: [
      { to: "/contacts", icon: Users, i18nKey: "nav.contacts" },
      { to: "/contacts/import", icon: Upload, i18nKey: "nav.contacts_import" },
      { to: "/reports", icon: BarChart3, i18nKey: "nav.reports" },
    ],
  },
  {
    i18nKey: "nav.section.tools",
    items: [
      { to: "/mnp", icon: ArrowRightLeft, i18nKey: "nav.portability" },
      { to: "/otp", icon: Lock, i18nKey: "nav.otp_2fa" },
      { to: "/qr", icon: QrCode, i18nKey: "nav.qr_code" },
      { to: "/attachments", icon: Paperclip, i18nKey: "nav.attachments" },
            { to: "/short-url", icon: Link2, i18nKey: "nav.short_url" },
      { to: "/mail2sms", icon: AtSign, i18nKey: "nav.mail2sms" },
      { to: "/sms2mail", icon: Forward, i18nKey: "nav.sms2mail" },
    ],
  },
  {
    i18nKey: "nav.section.services",
    items: [
      {
        to: "/mobile-advertising",
        icon: Megaphone,
        i18nKey: "nav.mobile_advertising",
        badge: { kind: "tag", key: "nav.on_request" },
      },
      {
        to: "/robcall",
        icon: Phone,
        i18nKey: "nav.robcall",
        badge: { kind: "tag", key: "nav.on_request" },
      },
      { to: "/esim", icon: CreditCard, i18nKey: "nav.esim_store" },
    ],
  },
  {
    i18nKey: "nav.section.billing",
    items: [
      { to: "/wallet", icon: Wallet, i18nKey: "nav.wallet" },
      { to: "/invoices", icon: Receipt, i18nKey: "nav.invoices" },
      { to: "/prices", icon: Tags, i18nKey: "nav.prices" },
    ],
  },
  {
    i18nKey: "nav.section.developer",
    items: [
      { to: "/api-keys", icon: KeyRound, i18nKey: "nav.api_keys" },
      { to: "/webhooks", icon: Webhook, i18nKey: "nav.webhooks" },
      { to: "/api-playground", icon: Terminal, i18nKey: "nav.api_playground" },
      { to: "/integrations", icon: Plug, i18nKey: "nav.integrations" },
      { to: "/help", icon: HelpCircle, i18nKey: "nav.help" },
    ],
  },
  {
    items: [
      { to: "/settings", icon: Settings, i18nKey: "nav.settings" },
    ],
  },
];

/**
 * SidebarNav — the navigation body (logo + grouped links), reused by the
 * desktop <aside> and the mobile drawer. onNavigate fires after a link is
 * activated so a drawer can close itself; on desktop it is omitted.
 */
export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const auth = useAuth();
  // ADMIN section visibility is restricted to platform_owner only.
  // Per-tenant `owner` and `admin` roles do NOT see this section because
  // it surfaces cross-organization tools (e.g. listing all users).
  const isPlatformOwner = auth.user?.role === "platform_owner";
  // SU2b-1 (23-Oct-2026): a sub-account has no billing area of its own (owner funds it) -- Wallet (own balance + ledger)
  // and Prices stay, Invoices goes. Server-side the invoice routes are scoped to the member (none exist).
  const isMember = auth.user?.role === "member";

  const groups: NavGroup[] = isPlatformOwner
    ? [
        ...GROUPS,
        {
          i18nKey: "nav.section.admin",
          items: [
            { to: "/admin/users", icon: ShieldCheck, i18nKey: "nav.admin_users" },
            { to: "/admin/invites", icon: Ticket, i18nKey: "nav.admin_invites" },
            { to: "/admin/senders", icon: BadgeCheck, i18nKey: "nav.admin_senders" },
            { to: "/admin/activity", icon: ScrollText, i18nKey: "nav.admin_activity" },
            { to: "/admin/health", icon: Activity, i18nKey: "nav.admin_health" },
          ],
        },
      ]
    : isMember
      ? GROUPS.map((g) => (g.i18nKey === "nav.section.billing" ? { ...g, items: g.items.filter((it) => it.to !== "/invoices") } : g))
      : GROUPS;

  return (
    <>
      {/* Logo */}
      <div className="flex h-20 items-center justify-center px-5">
        <Logo />
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        {groups.map((group, idx) => (
          <div key={idx} className={idx === 0 ? "" : "mt-4"}>
            {group.i18nKey ? (
              <div className="mb-1.5 mt-2 px-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-subtle">
                {t(group.i18nKey)}
              </div>
            ) : null}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavRow key={item.to} item={item} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>
    </>
  );
}

export default function Sidebar() {
  return (
    <aside className="hidden w-[220px] shrink-0 flex-col border-r border-border bg-surface-card md:flex">
      <SidebarNav />
    </aside>
  );
}

function Logo() {
  return (
    <div className="flex items-center">
      {/* D-BRAND-LOGO (7-Jul): first-party static asset (sendSMS infra only), WebP
          for browsers; intrinsic width/height reserve aspect ratio (zero CLS). */}
      <img
        src="/email/logo-sendsms-320.webp"
        width={320}
        height={199}
        alt="sendSMS"
        className="w-[120px] h-auto"
      />
    </div>
  );
}

function NavRow({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const label = t(item.i18nKey);

  return (
    <NavLink
      to={item.to}
      end
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "group flex h-9 items-center gap-3 rounded-md px-3 text-caption transition-colors",
          "focus:outline-none focus-visible:shadow-focus",
          isActive
            ? "bg-brand-50 font-medium text-brand-700"
            : "text-ink hover:bg-surface-subtle/60",
        )
      }
    >
      <Icon className="h-[15px] w-[15px] shrink-0 stroke-[1.75]" aria-hidden="true" />
      <span className="flex-1 truncate">{label}</span>
      {item.badge ? <NavBadge badge={item.badge} /> : null}
    </NavLink>
  );
}

function NavBadge({
  badge,
}: {
  badge: NonNullable<NavItem["badge"]>;
}) {
  const { t } = useTranslation();

  if (badge.kind === "count") {
    return (
      <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-50 px-1.5 text-[10px] font-semibold tabular-nums text-brand-700">
        {badge.value}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-pill border border-border px-1.5 py-px text-[8.5px] font-semibold uppercase tracking-[0.06em] text-ink-subtle">
      {t(badge.key)}
    </span>
  );
}
